var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed, observable, makeObservable } from 'mobx';
import { deserialize } from '../../utils/serializr/serializr';
import { AppClock } from './modules/appClock/AppClock';
import { AppOnlineCount } from './modules/AppOnlineCount';
import { User } from '../user/User';
import { Learnware } from '../learnware/Learnware';
import { AppMessage } from './modules/AppMessage';
import { AppNotification } from './modules/AppNotification';
import { AppConfig } from './modules/AppConfig';
import { AppSpeech } from './modules/AppSpeech';
import { AppPage } from './modules/AppPage';
import { AppInfo } from './modules/AppInfo';
import { AppDebug } from './modules/AppDebug';
import { AppMeasure } from './modules/AppMeasure';
import { Actor } from '../actor/Actor';
import { AppHostname } from './modules/AppHostname';
import { AppCookies } from './modules/AppCookies';
import { memoize } from '../../utils/mobx/decorators/memoize';
import { Director } from '../director/Director';
import { runtime } from '../runtime/Runtime';
import { AppInspect } from './modules/AppInspect';
import { AppAds } from './modules/AppAds';
import { AppEvents } from './modules/AppEvents';
import { AppBoot } from './modules/AppBoot';
import { AppMode } from './modules/AppMode';
import { cachedSet } from '../../utils/mobx/decorators/customComputed';
import { Speak } from '../speak/Speak';
import { Stage } from '../stage/Stage';
import { AppPlugins } from './modules/AppPlugins';
import { AppUrl } from './modules/AppUrl';
import { AppTrack } from './modules/AppTrack';
import { Menubar } from '../../areas/Menubar/Menubar';
import { AppPremise } from './modules/AppPremise';
import { Memory } from '../memory/Memory';
import { AppTray } from '../tray/AppTray';
import { AppAPI } from './api/AppAPI';
import { Directive } from '../directive/Directive';
import { Media } from '../media/Media';
import { Asking } from '../asking/Asking';
export class App extends Actor {
    type = 'app';
    constructor(init = { parent: undefined }) {
        super(init);
        makeObservable(this);
        if (runtime.isBrowserRuntime) {
            window.app = this;
            this.director = new Director(this);
        }
    }
    plugin = {};
    appBoot = new AppBoot(this);
    appCookies = new AppCookies(this);
    message = new AppMessage(this);
    notification = new AppNotification(this);
    appConfig = new AppConfig(this);
    appClock = new AppClock(this);
    appSpeech = new AppSpeech(this);
    appPage = new AppPage(this);
    appMode = new AppMode(this);
    appMeasure = new AppMeasure(this);
    appInfo = new AppInfo(this);
    appDebug = new AppDebug(this);
    appHost = new AppHostname(this);
    appInspect = new AppInspect(this);
    appAds = new AppAds(this);
    appEvents = new AppEvents(this);
    runtime = runtime;
    get appUrl() {
        return new AppUrl(this);
    }
    get tray() {
        return new AppTray(this);
    }
    get funnel() {
        return this.tray.funnel;
    }
    get history() {
        return this.tray.history;
    }
    get pose() {
        return this.tray.pose;
    }
    get asking() {
        return new Asking(this);
    }
    get appPlugins() {
        return new AppPlugins(this);
    }
    get memory() {
        return new Memory(this);
    }
    get multi() {
        return new runtime.solidPackages.Multi.classes.Multi(this);
    }
    get appId() {
        return this.id;
    }
    get importingChildren() {
        // question scene
        return super.importingChildren.concat(this.location, this.question);
    }
    get exportingChildren() {
        return [...super.exportingChildren, this.stage];
    }
    get user() {
        return new User(this);
    }
    get pathURI() {
        return runtime.solidBrowserRuntime.browserLocation.pathURI;
    }
    set pathURI(pathURI) {
        if (runtime.isBrowserRuntime) {
            runtime.solidBrowserRuntime.browserLocation.pathURI = pathURI;
        }
    }
    get learnware() {
        return new Learnware(this);
    }
    get track() {
        return new AppTrack(this);
    }
    get location() {
        return this.model.stream.location[this.pathURI];
    }
    set location(location) {
        if (location)
            this.pathURI = location.pathURI;
    }
    get stage() {
        return Stage.fromSerialized({ parent: this });
    }
    get topic() {
        return !this.location || this.location.type === 'topic' ? this.location : null;
    }
    set topic(topic) {
        if (topic)
            this.pathURI = topic.pathURI;
    }
    get path() {
        return !this.location || this.location.type === 'path' ? this.location : null;
    }
    set path(path) {
        if (path)
            this.pathURI = path.pathURI;
    }
    get solidTopic() {
        return this.topic;
    }
    get solidQuestion() {
        return this.question;
    }
    get bundle() {
        return this.exportingTree.allExportingActors
            .filter(b => b.type === 'bundle')
            .reduce((newObj, b) => {
            newObj[b.name] = b;
            return newObj;
        }, {});
    }
    get edit() {
        return runtime.streamedPackages.Edit && new runtime.streamedPackages.Edit.classes.Edit(this);
    }
    get solidEdit() {
        return this.edit;
    }
    get appOnlineCount() {
        return new AppOnlineCount(this);
    }
    get menubar() {
        return new Menubar(this);
    }
    get menubarUser() {
        return this.menubar.menubarUser;
    }
    get menubarPath() {
        return this.menubar.menubarPath;
    }
    get menubarRight() {
        return this.menubar.menubarRight;
    }
    get menubarProgress() {
        return this.menubar.menubarProgress;
    }
    get menubarEdit() {
        return this.menubar.solidMenubarEdit;
    }
    director;
    get directive() {
        return new Directive(this);
    }
    get media() {
        return new Media(this);
    }
    get mediaPlayer() {
        return this.media.mediaPlayer;
    }
    get hear() {
        if (runtime.isServerRuntime)
            return null;
        return runtime.solidBrowserRuntime.browserHear;
    }
    get recognition() {
        return this.hear.recognition;
    }
    get aural() {
        return this.hear.aural;
    }
    get listen() {
        return this.hear.listen;
    }
    get speak() {
        return new Speak(this);
    }
    get cycle() {
        return this.tray.trayCycles.frontalCycle;
    }
    get api() {
        return new AppAPI(this);
    }
    get appPremise() {
        return new AppPremise(this);
    }
    get textsizer() {
        return this.director.textSizer;
    }
    get editModal() {
        return this.solidEdit.editModal;
    }
    get progress() {
        return this.menubar.menubarProgress;
    }
    get appGrade() {
        return this.cycle.cycleGrade;
    }
    get userId_stringSafe() {
        return (this.user && this.user.id_string) || '';
    }
    get editQuestion() {
        if (!this.topic || !runtime.availablePackages.Edit)
            return undefined;
        if (!this.solidEdit.editCenter.centerEditItem)
            return undefined;
        if (this.solidEdit.editCenter.centerEditItem.type !== 'question')
            return undefined;
        return this.solidEdit.editCenter.centerEditItem;
    }
    set editQuestion(question) {
        if (question && this.topic)
            this.topic.topicEdit.questionList.setEditQuestionById(question.id);
    }
    get answer() {
        if (!this.stage.itemOnStage)
            return null;
        if (this.stage.itemOnStage.type !== 'answer')
            return null;
        return this.stage.itemOnStage;
        // return this.cycleHistory.showAnswer || this.current.answer;
    }
    set answer(answer) {
        this.directive.directivePoseQuestion.poseAnswer(answer);
    }
    get item() {
        return this.stage.itemOnStage;
    }
    get question() {
        if (this.appInfo.isUserCurrentlyEditing || this.appMode.isInPreviewMode)
            return this.editQuestion;
        return this.answer && this.answer.question;
    }
    get questionId() {
        if (!this.question)
            return undefined;
        return this.question.id;
    }
    set questionId(questionId) {
        console.log('set questionid manually', questionId);
        if (!questionId)
            return;
        this.directive.directivePoseQuestion.poseQuestionById(questionId);
    }
    get answerId() {
        if (!this.answer)
            return undefined;
        return this.answer.id;
    }
    set answerId(answerId) {
        console.log('set answerid manually', answerId);
        this.directive.directivePoseQuestion.stageAnswerById(answerId);
    }
    preloadQuestionId(questionId) {
        // API used by App
        this.tray.funnel.funnelPreload.preloadQuestionId(questionId);
    }
    static fromSerialized(ser = {}) {
        return deserialize(App, ser);
    }
    static get serializeInfo() {
        return {
            extends: Actor.serializeInfo,
            props: {},
        };
    }
}
__decorate([
    observable
], App.prototype, "plugin", void 0);
__decorate([
    memoize
], App.prototype, "appUrl", null);
__decorate([
    memoize
], App.prototype, "tray", null);
__decorate([
    memoize
], App.prototype, "asking", null);
__decorate([
    memoize
], App.prototype, "appPlugins", null);
__decorate([
    memoize
], App.prototype, "memory", null);
__decorate([
    memoize
], App.prototype, "multi", null);
__decorate([
    cachedSet
], App.prototype, "importingChildren", null);
__decorate([
    memoize
], App.prototype, "user", null);
__decorate([
    computed
], App.prototype, "pathURI", null);
__decorate([
    memoize
], App.prototype, "learnware", null);
__decorate([
    memoize
], App.prototype, "track", null);
__decorate([
    computed
], App.prototype, "location", null);
__decorate([
    memoize
], App.prototype, "stage", null);
__decorate([
    computed
], App.prototype, "path", null);
__decorate([
    memoize
], App.prototype, "edit", null);
__decorate([
    memoize
], App.prototype, "appOnlineCount", null);
__decorate([
    memoize
], App.prototype, "menubar", null);
__decorate([
    memoize
], App.prototype, "directive", null);
__decorate([
    memoize
], App.prototype, "media", null);
__decorate([
    memoize
], App.prototype, "api", null);
__decorate([
    memoize
], App.prototype, "appPremise", null);
__decorate([
    computed
], App.prototype, "editQuestion", null);
__decorate([
    computed
], App.prototype, "answer", null);
__decorate([
    computed
], App.prototype, "question", null);
__decorate([
    memoize
], App, "serializeInfo", null);
